import { Calendar, CheckCircle2, Clock, Trash2, MessageCircle } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import EditAppointmentDialog from "./EditAppointmentDialog";

const STATUS_META = {
  pendiente: { label: "Pendiente", style: { background: "#FEF3C7", color: "#92400E" }, Icon: Clock },
  pendiente_pago: { label: "Esperando pago OXXO", style: { background: "#FEF3C7", color: "#92400E" }, Icon: Clock },
  confirmada: { label: "Confirmada", style: { background: "var(--mint-dark)", color: "#fff" }, Icon: CheckCircle2 },
  pago_expirado: { label: "Pago expirado", style: { background: "#FEE2E2", color: "#991B1B" }, Icon: Clock },
};

const pill = "inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold";
const actionBtn = "inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-semibold transition-transform hover:scale-[1.03]";

function fmtDate(iso) {
  try { return new Date(iso).toLocaleString("es-MX", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
}

function waLink(a) {
  let digits = a.telefono.replace(/\D/g, "");
  if (digits.length === 10) digits = "52" + digits;
  const msg = `Hola ${a.nombre}, te escribo de parte de Rebeca García (Odontología) sobre tu cita del ${a.fecha_preferida} a las ${a.horario_preferido}.`;
  return `https://wa.me/${digits}?text=${encodeURIComponent(msg)}`;
}

function DeleteAppointmentButton({ a, onDelete }) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <button data-testid={`delete-appointment-${a.id}`} aria-label="Eliminar cita"
          className="inline-flex items-center gap-1.5 rounded-full px-3 py-2 text-xs font-semibold transition-colors hover:bg-red-50"
          style={{ border: "1px solid #FECACA", color: "#991B1B" }}>
          <Trash2 size={13} /> Eliminar
        </button>
      </AlertDialogTrigger>
      <AlertDialogContent className="rounded-[1.5rem]" data-testid={`delete-dialog-${a.id}`}>
        <AlertDialogHeader>
          <AlertDialogTitle className="font-display">¿Eliminar la cita de {a.nombre}?</AlertDialogTitle>
          <AlertDialogDescription>
            {a.fecha_preferida} a las {a.horario_preferido} · {a.servicio}. Esta acción no se puede deshacer y libera el horario.
            {a.pagado ? " El pago NO se reembolsa automáticamente; hazlo desde Stripe o PayPal si corresponde." : ""}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-full" data-testid={`cancel-delete-${a.id}`}>Cancelar</AlertDialogCancel>
          <AlertDialogAction onClick={() => onDelete(a)} data-testid={`confirm-delete-${a.id}`}
            className="rounded-full bg-red-700 text-white hover:bg-red-800">Sí, eliminar</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function Badges({ a }) {
  const meta = STATUS_META[a.status] || STATUS_META.pendiente;
  return (
    <div>
      <div className="flex items-center gap-3">
        <h3 className="font-display text-xl font-bold" style={{ color: "var(--text-dark)" }}>{a.nombre}</h3>
        <span className={pill} style={meta.style} data-testid={`status-badge-${a.id}`}><meta.Icon size={12} />{meta.label}</span>
      </div>
      <span className="mt-2 inline-flex items-center gap-2">
        <span className={pill} style={{ background: "var(--mint-light)", color: "var(--mint-dark)" }}>{a.servicio}</span>
        {a.pagado && <span className={pill + " text-white"} style={{ background: "var(--mint-dark)" }} data-testid={`paid-badge-${a.id}`}>Pagado $200</span>}
        {a.conflicto && <span className={pill} style={{ background: "#FEE2E2", color: "#991B1B" }} data-testid={`conflict-badge-${a.id}`}>Conflicto de horario: reagendar</span>}
      </span>
    </div>
  );
}

function Actions({ a, onToggle, onDelete, onSaved, onUnauthorized }) {
  const confirmed = a.status === "confirmada";
  return (
    <div className="flex flex-col items-end gap-2">
      <span className="text-xs" style={{ color: "var(--text-muted)" }}>{fmtDate(a.created_at)}</span>
      <div className="flex items-center gap-2">
        <a href={waLink(a)} target="_blank" rel="noopener noreferrer" data-testid={`whatsapp-${a.id}`}
          className={actionBtn + " text-white"} style={{ background: "#25D366" }}>
          <MessageCircle size={13} /> WhatsApp
        </a>
        <button onClick={() => onToggle(a)} data-testid={`toggle-status-${a.id}`} className={actionBtn}
          style={confirmed ? { border: "1px solid var(--border)", color: "var(--text-dark)" } : { background: "var(--mint-dark)", color: "#fff" }}>
          {confirmed ? <><Clock size={13} /> Marcar pendiente</> : <><CheckCircle2 size={13} /> Confirmar</>}
        </button>
        <EditAppointmentDialog a={a} onSaved={onSaved} onUnauthorized={onUnauthorized} />
        <DeleteAppointmentButton a={a} onDelete={onDelete} />
      </div>
    </div>
  );
}

export default function AppointmentCard({ a, onToggle, onDelete, onSaved, onUnauthorized }) {
  const confirmed = a.status === "confirmada";
  return (
    <div className={`rounded-[1.5rem] border bg-white p-6 transition-colors ${confirmed ? "border-[var(--mint)]" : "border-[var(--border)]"}`} data-testid={`appointment-${a.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <Badges a={a} />
        <Actions a={a} onToggle={onToggle} onDelete={onDelete} onSaved={onSaved} onUnauthorized={onUnauthorized} />
      </div>
      <div className="mt-4 grid sm:grid-cols-2 gap-3 text-sm">
        <p><span className="font-semibold" style={{ color: "var(--mint-dark)" }}>Tel / WhatsApp:</span> <span style={{ color: "var(--text-dark)" }}>{a.telefono}</span></p>
        <p><span className="font-semibold" style={{ color: "var(--mint-dark)" }}>Correo:</span> <span style={{ color: "var(--text-dark)" }}>{a.correo}</span></p>
        <p className="flex items-center gap-1.5"><Calendar size={14} style={{ color: "var(--mint-dark)" }} /> <span style={{ color: "var(--text-dark)" }}>{a.fecha_preferida} — {a.horario_preferido}</span></p>
      </div>
      {a.comentarios ? (
        <p className="mt-3 rounded-2xl bg-[var(--bg)] p-3 text-sm italic" style={{ color: "var(--text-muted)" }}>"{a.comentarios}"</p>
      ) : null}
    </div>
  );
}
